import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { BigSlide, SmallCard } from "./featured/FeaturedSlide";

let cfgPromise = null;
export const loadFeaturedCfg = (fresh) => {
  if (fresh || !cfgPromise) cfgPromise = api.get("/site/featured-box").then((r) => r.data).catch(() => null);
  return cfgPromise;
};

const MOTION = {
  fade: { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } },
  slide: { initial: { opacity: 0, x: 60 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -60 } },
  zoom: { initial: { opacity: 0, scale: 1.06 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.97 } },
};

const useWidth = () => {
  const [w, setW] = useState(() => window.innerWidth);
  useEffect(() => { const f = () => setW(window.innerWidth); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  return w;
};

// mode: "auto" follows the screen; "desktop" / "mobile" force one (admin preview). cfg/previewItems are only passed by the admin preview.
export const FeaturedCarousel = ({ category = "", embedded = false, title, cfg: forced, previewItems, mode = "auto", onItems }) => {
  const { lang } = useLanguage();
  const [fetched, setFetched] = useState(null);
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [hover, setHover] = useState(false);
  const width = useWidth();
  const c = forced || fetched;

  useEffect(() => { if (!forced) loadFeaturedCfg().then(setFetched); }, [forced]);
  useEffect(() => {
    if (previewItems) return setItems(previewItems);
    const qs = category && category !== "all" ? `?category=${encodeURIComponent(category)}` : "";
    setIdx(0);
    api.get(`/listings/featured${qs}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [category, previewItems]);

  const list = c ? items.slice(0, c.max_slides) : [];
  const n = list.length;
  const isMobile = mode === "mobile" || (mode === "auto" && width < 768);
  const per = !c ? 1 : isMobile ? 1 : Math.min(c.per_view, mode === "auto" && width < 1024 ? 2 : 4, Math.max(n, 1));
  const rotates = n > per;
  const paused = hover && c?.pause_hover;
  const listKey = list.map((l) => l.id).join(",");
  useEffect(() => {
    if (!onItems || !c) return;
    onItems(c.enabled && c.allow_repeats === false && listKey ? listKey.split(",") : []);
  }, [onItems, c, listKey]);

  useEffect(() => {
    if (!c || !rotates || paused) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % n), c.speed * 1000);
    return () => clearInterval(id);
  }, [c, rotates, paused, n]);

  if (!c || !c.enabled || !n) return null;
  const pick = (k) => (c[`${k}_en`] ? (lang === "fr" && c[`${k}_fr`]) || c[`${k}_en`] : "");
  const t = { badge: pick("badge"), button: pick("button") };
  const scale = embedded ? 0.67 : 1;
  const hd = `${Math.round(c.height_desktop * scale)}px`, hm = `${Math.round(c.height_mobile * (embedded ? 0.8 : 1))}px`;
  const go = (k) => setIdx(((k % n) + n) % n);
  const track = (id) => { api.post(`/listings/${id}/featured-click`).catch(() => {}); };
  const shown = Array.from({ length: per }, (_, k) => list[(idx + k) % n]);
  const anim = MOTION[c.transition] || MOTION.fade;
  const arrow = "absolute top-1/2 -translate-y-1/2 h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-white/90 hover:bg-white text-[#12234A] flex items-center justify-center shadow z-10";

  return (
    <section className={embedded ? "mt-5" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 sm:mt-10"} data-testid={embedded ? "category-featured-carousel" : "featured-carousel"}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} data-per-view={per} data-paused={paused ? "yes" : "no"}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-2 sm:mb-3">
        <Star size={18} style={{ fill: c.accent_color, color: c.accent_color }} />
        <h2 className="font-display text-base sm:text-lg font-bold text-[#12234A]" data-testid="featured-title">{title || pick("title")}</h2>
        {!embedded && pick("subtitle") && <span className="hidden sm:inline text-xs text-slate-400" data-testid="featured-subtitle">{pick("subtitle")}</span>}
      </div>
      <div className={`relative ${per === 1 ? "rounded-2xl sm:rounded-3xl overflow-hidden border" : ""}`} style={per === 1 ? { background: c.panel_color, borderColor: `${c.accent_color}4D` } : undefined}>
        {per === 1 ? (
          <AnimatePresence mode="wait">
            <motion.div key={shown[0].id} {...anim} transition={{ duration: 0.5 }}>
              <BigSlide item={shown[0]} c={c} t={t} hm={hm} hd={hd} mode={mode} onClick={() => track(shown[0].id)} />
            </motion.div>
          </AnimatePresence>
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${per}, minmax(0, 1fr))` }}>
            {shown.map((it, k) => (
              <AnimatePresence mode="wait" key={k}>
                <motion.div key={`${it.id}-${k}`} {...anim} transition={{ duration: 0.45, delay: k * 0.05 }}>
                  <SmallCard item={it} c={c} t={t} hd={hd} onClick={() => track(it.id)} />
                </motion.div>
              </AnimatePresence>
            ))}
          </div>
        )}
        {rotates && c.show_arrows && (
          <>
            <button onClick={() => go(idx - 1)} data-testid="featured-prev" aria-label="Previous" className={`${arrow} ${per === 1 ? "left-2 sm:left-3" : "-left-3"}`}><ChevronLeft size={20} /></button>
            <button onClick={() => go(idx + 1)} data-testid="featured-next" aria-label="Next" className={`${arrow} ${per === 1 ? "right-2 sm:right-3" : "-right-3"}`}><ChevronRight size={20} /></button>
          </>
        )}
        {rotates && c.show_dots && (
          <div className={`flex gap-1.5 justify-center ${per === 1 ? "absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2" : "mt-3"}`} data-testid="featured-dots">
            {list.map((_, i) => (
              <button key={i} onClick={() => go(i)} data-testid={`featured-dot-${i}`} aria-label={`Slide ${i + 1}`}
                className="h-2 rounded-full transition-all" style={{ width: i === idx ? 24 : 8, background: i === idx ? c.accent_color : per === 1 ? "rgba(255,255,255,0.5)" : "#cbd5e1" }} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
